// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { EmailDesignTemplateModel } from '../../../state_manager/data_models/email_design_template_model.ts'
import { FilterValue, SortConfig } from '../../../state_manager/interfaces/email.ts'
import {
  fetchEmailDesignTemplatesFiltered,
  fetchEmailDesignTemplateFilterOptions
} from '../services/emailDesignTemplateService.ts'

export const DEFAULT_TEMPLATES_SORTING: SortConfig[] = [
  { key: 'email_design_templates__date_modified', direction: 'desc' }
]

export interface UseEmailDesignTemplatesArgs {
  initialSorting?: SortConfig[]
  initialFilters?: FilterValue[]
}

/**
 * useEmailDesignTemplates — Templates tab inside the Email module.
 *
 * Mirrors useEmailCampaigns: its own data RPC (view__email_design_templates),
 * screen-local state. Filter options are fetched once on mount (the body_source
 * universe is static today).
 */
export const useEmailDesignTemplates = ({
  initialSorting = DEFAULT_TEMPLATES_SORTING,
  initialFilters = []
}: UseEmailDesignTemplatesArgs = {}) => {
  const [templatesData, setTemplatesData] = useState<EmailDesignTemplateModel[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [activeSorting, setActiveSorting] = useState<SortConfig[]>(initialSorting)
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>(initialFilters)
  const [filterOptions, setFilterOptions] = useState<
    { value: string; label: string }[] | null
  >(null)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const handleFetchTemplates = useCallback(
    async (
      sorting: SortConfig[] = DEFAULT_TEMPLATES_SORTING,
      filters: FilterValue[] = []
    ) => {
      try {
        setTemplatesLoading(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchEmailDesignTemplatesFiltered(
          sorting && sorting.length > 0 ? sorting : DEFAULT_TEMPLATES_SORTING,
          filters ?? [],
          1,
          null,
          token
        )
        if (cancelledRef.current) return
        if (response.error && process.env.NODE_ENV === 'development') {
          console.error(response.error)
        }
        setTemplatesData(response.data)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error(e)
      } finally {
        if (!cancelledRef.current) setTemplatesLoading(false)
      }
    },
    []
  )

  // Filter options — once on mount.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      if (!token) return
      const response = await fetchEmailDesignTemplateFilterOptions(token)
      if (cancelled) return
      if (response.data?.email_design_templates__body_source) {
        setFilterOptions(response.data.email_design_templates__body_source)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const token = getAccessToken(store.getState().auth)
    if (token) handleFetchTemplates(activeSorting, activeFilters)
  }, [activeFilters, activeSorting, handleFetchTemplates])

  const handleSetSorting = useCallback((sorting: SortConfig[]) => {
    setActiveSorting(sorting)
  }, [])

  const handleSetFilters = useCallback((filters: FilterValue[]) => {
    setActiveFilters(filters)
  }, [])

  const refetch = useCallback(
    () => handleFetchTemplates(activeSorting, activeFilters),
    [handleFetchTemplates, activeSorting, activeFilters]
  )

  return {
    templatesData,
    templatesLoading,
    activeSorting,
    activeFilters,
    filterOptions,
    handleSetSorting,
    handleSetFilters,
    refetch
  }
}
